import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useWebSocket } from "../../hooks/useWebSocket";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Card, CardContent } from "../../components/ui/card";
import { 
  ChefHat, Clock, Volume2, VolumeX, ArrowRight,
  LogOut, RefreshCw, User, Utensils, AlertCircle, Coffee, Check, Printer
} from "lucide-react";
import { cn, formatCurrency } from "../../lib/utils";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getTimeSince = (dateStr) => {
  if (!dateStr) return "—";
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  return `${Math.floor(diff / 3600)}h`;
};

// ===== Kitchen ticket auto-print =====
// Builds a thermal-receipt-style ticket HTML and prints via hidden iframe.
// Works on mobile/tablet and avoids popup blockers.
const printKitchenTicket = ({ order, items, restaurantName, kitchenType }) => {
  if (!items || items.length === 0) return;
  try {
    const orderNum = order.order_number?.split("-")[1] || order.order_number || "";
    const tableLabel = order.order_type === "eat_in" ? `${order.qr_type ? order.qr_type.toUpperCase().replace(/_/g, " ") : "TABLE"} ${order.table_number || "—"}` : "TAKEAWAY";
    const subLine = order.order_type === "eat_in"
      ? [order.qr_name, order.floor, order.zone, order.branch].filter(Boolean).join(" · ")
      : "";
    const ts = new Date().toLocaleString("en", { dateStyle: "short", timeStyle: "short" });
    const rows = items.map(i => {
      const extras = (i.extras_details || i.extras || []).map(e => `<div class="x">+ ${e.name}</div>`).join("");
      const variant = i.variant_name ? `<div class="x">[${i.variant_name}]</div>` : "";
      const notes = i.notes ? `<div class="n">NOTE: ${i.notes}</div>` : "";
      const prep = i.prep_time ? `<span class="t">${i.prep_time}m</span>` : "";
      return `<div class="row"><div class="line"><span class="q">${i.quantity}×</span><span class="nm">${i.name}</span>${prep}</div>${variant}${extras}${notes}</div>`;
    }).join("");

    // Remove any old kitchen iframe
    const existing = document.getElementById("dalabsom-kitchen-print-iframe");
    if (existing) existing.remove();

    const iframe = document.createElement("iframe");
    iframe.id = "dalabsom-kitchen-print-iframe";
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.opacity = "0";
    document.body.appendChild(iframe);

    const html = `<!doctype html><html><head><title>Kitchen Ticket #${orderNum}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Courier New', ui-monospace, monospace; padding: 6px; width: 80mm; max-width: 80mm; color: #000; font-size: 12px; line-height: 1.35; }
        .hd { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 6px; margin-bottom: 6px; }
        .hd .t1 { font-size: 26px; font-weight: 900; letter-spacing: 1px; }
        .hd .t2 { font-size: 13px; font-weight: bold; margin-top: 2px; }
        .meta { display: flex; justify-content: space-between; font-size: 11px; border-bottom: 1px dashed #000; padding-bottom: 4px; margin-bottom: 6px; font-weight: bold; }
        .row { border-bottom: 1px dashed #ccc; padding: 4px 0; }
        .row:last-child { border-bottom: none; }
        .line { display: flex; align-items: center; gap: 4px; font-size: 16px; font-weight: 900; }
        .q { background: #000; color: #fff; padding: 1px 6px; border-radius: 3px; min-width: 28px; text-align: center; }
        .nm { flex: 1; }
        .t { font-size: 10px; background: #eee; padding: 1px 5px; border-radius: 3px; font-weight: normal; }
        .x { font-size: 12px; margin-left: 38px; color: #444; }
        .n { font-size: 12px; margin-left: 38px; margin-top: 2px; padding: 3px 5px; background: #fff3cd; border: 1px solid #000; font-weight: bold; }
        .ft { text-align: center; margin-top: 8px; font-size: 10px; color: #666; }
        @page { size: 80mm auto; margin: 0; }
        @media print { body { padding: 4px; } }
      </style></head><body>
      <div class="hd">
        <div class="t1">${tableLabel}</div>
        ${subLine ? `<div class="t2">${subLine}</div>` : ""}
        <div class="t2">#${orderNum} · ${kitchenType === "drink" ? "DRINKS" : kitchenType === "food" ? "FOOD" : "KITCHEN"}</div>
      </div>
      <div class="meta"><span>${restaurantName || "Dalabsom"}</span><span>${ts}</span></div>
      ${rows}
      <div class="ft">— Prepare immediately —</div>
    </body></html>`;

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    const doPrint = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        console.error("Kitchen print error:", err);
      }
      setTimeout(() => { try { iframe.remove(); } catch (_) {} }, 60000);
    };
    if (iframe.contentDocument.readyState === "complete") {
      setTimeout(doPrint, 200);
    } else {
      iframe.onload = () => setTimeout(doPrint, 200);
    }
  } catch (e) {
    console.error("printKitchenTicket failed:", e);
  }
};

const ITEM_STATUS_FLOW = {
  waiting: "preparing",
  preparing: "ready",
  ready: "served"
};

const ITEM_STATUS_LABELS = {
  waiting: "Start",
  preparing: "Ready",
  ready: "Served"
};

const ITEM_STATUS_COLORS = {
  waiting: "bg-blue-500 hover:bg-blue-600",
  preparing: "bg-amber-500 hover:bg-amber-600 text-black",
  ready: "bg-green-500 hover:bg-green-600"
};

const KitchenDashboard = ({ kitchenType = "all" }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoPrintTickets, setAutoPrintTickets] = useState(() => {
    const v = localStorage.getItem("kitchen_auto_print");
    return v === null ? true : v === "true";
  });
  const [restaurant, setRestaurant] = useState(null);
  const [activeTab, setActiveTab] = useState("orders");
  const [menuData, setMenuData] = useState({ categories: [], items: [] });
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [menuPrepTimes, setMenuPrepTimes] = useState({});
  const bellSound = useRef(null);

  const titles = { all: "Kitchen", food: "Food Kitchen", drink: "Drinks & Beverages" };
  const icons = { all: ChefHat, food: ChefHat, drink: Coffee };
  const colors = { all: "bg-orange-500", food: "bg-amber-500", drink: "bg-teal-500" };
  const KitchenIcon = icons[kitchenType];

  // Sound
  useEffect(() => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    bellSound.current = () => {
      if (!soundEnabled) return;
      const play = (f, t, d) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.connect(g); g.connect(ctx.destination); o.frequency.value = f; o.type = "sine"; g.gain.setValueAtTime(0.3, t); g.gain.exponentialRampToValueAtTime(0.01, t + d); o.start(t); o.stop(t + d); };
      const n = ctx.currentTime;
      if (kitchenType === "drink") { play(1046, n, 0.12); play(1318, n + 0.15, 0.12); play(1568, n + 0.3, 0.25); }
      else { play(830, n, 0.15); play(830, n + 0.2, 0.15); play(1046, n + 0.4, 0.3); }
    };
    return () => { ctx.close(); };
  }, [soundEnabled, kitchenType]);

  const playSound = useCallback(() => { if (soundEnabled && bellSound.current) bellSound.current(); }, [soundEnabled]);

  // Filter items by kitchen type
  const filterOrderItems = useCallback((order) => {
    if (kitchenType === "all") return order.items || [];
    return (order.items || []).filter(item => (item.category_type || "food") === kitchenType);
  }, [kitchenType]);

  // Only show orders that have items for this kitchen
  const hasItemsForKitchen = useCallback((order) => {
    return filterOrderItems(order).length > 0;
  }, [filterOrderItems]);

  // Get the "kitchen status" for an order based on only THIS kitchen's items
  const getKitchenStatus = useCallback((order) => {
    const items = filterOrderItems(order);
    if (items.length === 0) return "done";
    const statuses = items.map(i => i.item_status || "waiting");
    if (statuses.every(s => s === "served" || s === "cancelled")) return "done";
    if (statuses.every(s => s === "ready" || s === "served" || s === "cancelled")) return "ready";
    if (statuses.some(s => s === "preparing")) return "preparing";
    if (statuses.some(s => s === "ready")) return "preparing";
    return "new";
  }, [filterOrderItems]);

  const handleWebSocketMessage = useCallback((msg) => {
    if (msg.type === "new_order") {
      if (hasItemsForKitchen(msg.order)) {
        setOrders(prev => [msg.order, ...prev.filter(o => o.id !== msg.order.id)]);
        playSound();
        toast.success(`New Order #${msg.order.order_number.split("-")[1]}`, { duration: 5000 });
      }
    } else if (["order_status_update", "item_status_update", "order_updated", "payment_confirmed"].includes(msg.type)) {
      setOrders(prev => prev.map(o => o.id === msg.order.id ? msg.order : o));
    }
  }, [playSound, hasItemsForKitchen]);

  useWebSocket(user?.restaurant_id, handleWebSocketMessage);

  useEffect(() => { fetchOrders(); fetchRestaurant(); fetchPrepTimes(); const i = setInterval(fetchOrders, 30000); return () => clearInterval(i); }, []);

  const fetchRestaurant = async () => {
    try { const r = await axios.get(`${API}/restaurant/profile`); setRestaurant(r.data); }
    catch (e) { console.error("Failed to fetch restaurant profile:", e); }
  };

  const fetchPrepTimes = async () => {
    try {
      const r = await axios.get(`${API}/kitchen/menu`);
      const map = {};
      (r.data.items || []).forEach(item => { map[item.id] = item.prep_time || 15; });
      setMenuPrepTimes(map);
    } catch (e) { console.error("Failed to fetch prep times:", e); }
  };

  const fetchMenu = async () => {
    setLoadingMenu(true);
    try {
      const r = await axios.get(`${API}/kitchen/menu`);
      const d = r.data;
      if (kitchenType !== "all") {
        d.categories = d.categories.filter(c => (c.category_type || "food") === kitchenType);
        const catIds = new Set(d.categories.map(c => c.id));
        d.items = d.items.filter(i => catIds.has(i.category_id));
      }
      setMenuData(d);
    } catch (e) {
      console.error("Failed to fetch kitchen menu:", e);
      toast.error("Could not load menu");
    } finally { setLoadingMenu(false); }
  };

  const toggleItemAvailability = async (itemId) => {
    try {
      const r = await axios.put(`${API}/kitchen/items/${itemId}/availability`);
      setMenuData(prev => ({ ...prev, items: prev.items.map(i => i.id === itemId ? { ...i, is_available: r.data.is_available } : i) }));
      toast.success(r.data.is_available ? "Item available" : "Item unavailable");
    } catch (e) { toast.error("Failed to update"); }
  };

  const fetchOrders = async () => {
    try {
      const r = await axios.get(`${API}/orders`);
      // Show orders that have active items for this kitchen
      const active = r.data.filter(o => {
        if (!hasItemsForKitchen(o)) return false;
        const kStatus = getKitchenStatus(o);
        return kStatus !== "done";
      });
      setOrders(active);
    } catch (e) {
      console.error("Failed to fetch orders:", e);
      toast.error("Could not refresh orders");
    } finally { setLoading(false); }
  };

  // Update a single item's status
  const updateItemStatus = async (orderId, itemId, newStatus) => {
    try {
      // Capture pre-update order so we can grab item details for ticket
      const preOrder = orders.find(o => o.id === orderId);
      const preItem = preOrder?.items?.find(i => i.id === itemId);

      const r = await axios.put(`${API}/orders/${orderId}/items/${itemId}/status`, { item_status: newStatus });

      // Auto-print kitchen ticket when an item transitions to preparing
      if (newStatus === "preparing" && autoPrintTickets && preItem && (preItem.item_status || "waiting") === "waiting") {
        printKitchenTicket({
          order: r.data,
          items: [preItem],
          restaurantName: restaurant?.name,
          kitchenType,
        });
      }

      setOrders(prev => prev.map(o => o.id === orderId ? r.data : o));
      // Remove order from view if all this kitchen's items are done
      const kitchenItems = filterOrderItems(r.data);
      const allDone = kitchenItems.every(i => i.item_status === "served" || i.item_status === "cancelled");
      if (allDone) {
        setOrders(prev => prev.filter(o => o.id !== orderId));
        toast.success("All items done!");
      } else {
        toast.success(`${newStatus === "preparing" ? "Preparing" : newStatus === "ready" ? "Ready!" : "Served!"}`);
      }
    } catch (e) { toast.error("Failed to update item"); }
  };

  // Mark ALL items for this kitchen at once (bulk action)
  const bulkUpdateItems = async (orderId, newStatus) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;
    const items = filterOrderItems(order).filter(i => ITEM_STATUS_FLOW[i.item_status || "waiting"]);

    // Bulk auto-print: one ticket with all items being started
    if (newStatus === "preparing" && autoPrintTickets) {
      const startingItems = items.filter(i => (i.item_status || "waiting") === "waiting");
      if (startingItems.length > 0) {
        printKitchenTicket({
          order,
          items: startingItems,
          restaurantName: restaurant?.name,
          kitchenType,
        });
      }
    }

    for (const item of items) {
      await updateItemStatus(orderId, item.id, newStatus);
    }
  };

  // Manual print — print all currently active items for this kitchen for an order
  const printOrderTicket = (order) => {
    const items = filterOrderItems(order).filter(i => !["served", "cancelled"].includes(i.item_status || "waiting"));
    if (items.length === 0) { toast.error("No active items to print"); return; }
    printKitchenTicket({ order, items, restaurantName: restaurant?.name, kitchenType });
  };

  // Group orders by kitchen status
  const ordersByKitchenStatus = {
    new: orders.filter(o => getKitchenStatus(o) === "new"),
    preparing: orders.filter(o => getKitchenStatus(o) === "preparing"),
    ready: orders.filter(o => getKitchenStatus(o) === "ready"),
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className={cn("w-10 h-10 border-2 border-t-transparent rounded-full animate-spin", kitchenType === "drink" ? "border-teal-500" : "border-orange-500")} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" data-testid={`kitchen-dashboard-${kitchenType}`}>
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 px-4 lg:px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {restaurant?.logo_data ? (
              <img src={restaurant.logo_data} alt={restaurant.name} className="w-10 h-10 rounded-xl object-cover" />
            ) : (
              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", colors[kitchenType])}>
                <KitchenIcon className="w-6 h-6 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-lg font-bold text-white">{restaurant?.name || "Kitchen"}</h1>
              <p className="text-xs text-gray-400">{titles[kitchenType]}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Kitchen switch */}
            <div className="hidden md:flex items-center gap-1 bg-gray-800 rounded-full p-1">
              <Button size="sm" variant={kitchenType === "food" ? "default" : "ghost"}
                className={cn("rounded-full text-xs gap-1 h-7", kitchenType === "food" ? "bg-amber-500 text-black" : "text-gray-400")}
                onClick={() => navigate("/kitchen/food")}>
                <ChefHat className="w-3 h-3" /> Food
              </Button>
              <Button size="sm" variant={kitchenType === "drink" ? "default" : "ghost"}
                className={cn("rounded-full text-xs gap-1 h-7", kitchenType === "drink" ? "bg-teal-500 text-white" : "text-gray-400")}
                onClick={() => navigate("/kitchen/drinks")}>
                <Coffee className="w-3 h-3" /> Drinks
              </Button>
              <Button size="sm" variant={kitchenType === "all" ? "default" : "ghost"}
                className={cn("rounded-full text-xs gap-1 h-7", kitchenType === "all" ? "bg-orange-500 text-white" : "text-gray-400")}
                onClick={() => navigate("/kitchen")}>
                All
              </Button>
            </div>

            <div className="flex items-center gap-2 bg-gray-800 rounded-full px-3 py-1.5">
              <Printer className={cn("w-4 h-4", autoPrintTickets ? "text-green-400" : "text-gray-400")} />
              <Switch checked={autoPrintTickets}
                onCheckedChange={(v) => { setAutoPrintTickets(v); localStorage.setItem("kitchen_auto_print", v.toString()); }}
                className="data-[state=checked]:bg-green-500" data-testid="autoprint-toggle" />
            </div>
            <div className="flex items-center gap-2 bg-gray-800 rounded-full px-3 py-1.5">
              {soundEnabled ? <Volume2 className="w-4 h-4 text-green-400" /> : <VolumeX className="w-4 h-4 text-gray-400" />}
              <Switch checked={soundEnabled} onCheckedChange={setSoundEnabled} className="data-[state=checked]:bg-green-500" data-testid="sound-toggle" />
            </div>
            <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white h-8 w-8" onClick={fetchOrders}><RefreshCw className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" className="text-gray-400 hover:text-red-400 h-8 w-8" onClick={handleLogout} data-testid="logout-btn"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="mt-3">
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === "menu") fetchMenu(); }}>
            <TabsList className="bg-gray-800 h-9">
              <TabsTrigger value="orders" className={cn("text-xs data-[state=active]:text-white", kitchenType === "drink" ? "data-[state=active]:bg-teal-500" : "data-[state=active]:bg-orange-500")}>
                <KitchenIcon className="w-3 h-3 mr-1" /> Orders ({orders.length})
              </TabsTrigger>
              <TabsTrigger value="menu" className={cn("text-xs data-[state=active]:text-white", kitchenType === "drink" ? "data-[state=active]:bg-teal-500" : "data-[state=active]:bg-orange-500")}>
                <Utensils className="w-3 h-3 mr-1" /> Menu
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </header>

      <main className="p-4 lg:p-6">
        {activeTab === "orders" ? (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {/* NEW Column */}
            <KanbanColumn
              label="New" dotColor="bg-blue-500" orders={ordersByKitchenStatus.new}
              kitchenType={kitchenType} filterOrderItems={filterOrderItems} updateItemStatus={updateItemStatus} bulkUpdateItems={bulkUpdateItems} menuPrepTimes={menuPrepTimes} printOrderTicket={printOrderTicket}
            />
            {/* PREPARING Column */}
            <KanbanColumn
              label="Preparing" dotColor="bg-amber-500" orders={ordersByKitchenStatus.preparing}
              kitchenType={kitchenType} filterOrderItems={filterOrderItems} updateItemStatus={updateItemStatus} bulkUpdateItems={bulkUpdateItems} menuPrepTimes={menuPrepTimes} printOrderTicket={printOrderTicket}
            />
            {/* READY Column */}
            <KanbanColumn
              label="Ready" dotColor="bg-green-500" orders={ordersByKitchenStatus.ready}
              kitchenType={kitchenType} filterOrderItems={filterOrderItems} updateItemStatus={updateItemStatus} bulkUpdateItems={bulkUpdateItems} menuPrepTimes={menuPrepTimes} printOrderTicket={printOrderTicket}
            />
          </div>
        ) : (
          <div className="max-w-4xl">
            <div className="mb-4">
              <h2 className="text-lg font-bold text-white mb-1">Menu Availability</h2>
              <p className="text-gray-400 text-sm">Toggle items on/off. Disabled items won't appear on customer menu.</p>
            </div>
            {loadingMenu ? (
              <div className="flex items-center justify-center h-32"><div className={cn("w-8 h-8 border-2 border-t-transparent rounded-full animate-spin", kitchenType === "drink" ? "border-teal-500" : "border-orange-500")} /></div>
            ) : menuData.categories.length === 0 ? (
              <div className="text-center py-12 text-gray-500"><Utensils className="w-12 h-12 mx-auto mb-2 opacity-50" /><p>No menu items</p></div>
            ) : (
              <div className="space-y-4">
                {menuData.categories.map(cat => {
                  const items = menuData.items.filter(i => i.category_id === cat.id);
                  return (
                    <Card key={cat.id} className="bg-gray-800 border-gray-700">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-white font-semibold">{cat.name}</h3>
                          <Badge variant="outline" className="text-gray-400 border-gray-600">{items.length}</Badge>
                        </div>
                        <div className="space-y-2">
                          {items.map(item => (
                            <div key={item.id} className={cn("flex items-center justify-between p-3 rounded-lg", item.is_available ? "bg-gray-700/50" : "bg-red-900/30 border border-red-700")}>
                              <div className="flex items-center gap-2">
                                {!item.is_available && <AlertCircle className="w-4 h-4 text-red-400" />}
                                <span className={cn("font-medium text-sm", item.is_available ? "text-white" : "text-red-300")}>{item.name}</span>
                                <span className="text-gray-400 text-xs">{formatCurrency(item.price)}</span>
                              </div>
                              <Switch checked={item.is_available} onCheckedChange={() => toggleItemAvailability(item.id)} className="data-[state=checked]:bg-green-500" />
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

// ===== KANBAN COLUMN =====
const KanbanColumn = ({ label, dotColor, orders, kitchenType, filterOrderItems, updateItemStatus, bulkUpdateItems, menuPrepTimes, printOrderTicket }) => (
  <div className="flex-1 min-w-[340px] max-w-[420px]">
    <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-700">
      <div className="flex items-center gap-2">
        <div className={cn("w-2.5 h-2.5 rounded-full", dotColor)} />
        <span className="text-white font-semibold">{label}</span>
      </div>
      <Badge variant="outline" className="bg-gray-800 text-gray-300 border-gray-700 text-xs">{orders.length}</Badge>
    </div>
    <div className="space-y-3 overflow-y-auto max-h-[calc(100vh-200px)] pr-1">
      {orders.length === 0 ? (
        <div className="bg-gray-800/50 border border-dashed border-gray-700 rounded-lg p-8 text-center">
          <p className="text-gray-500 text-sm">No orders</p>
        </div>
      ) : orders.map(order => (
        <OrderCard key={order.id} order={order} kitchenType={kitchenType}
          filterOrderItems={filterOrderItems} updateItemStatus={updateItemStatus} bulkUpdateItems={bulkUpdateItems} menuPrepTimes={menuPrepTimes} printOrderTicket={printOrderTicket} />
      ))}
    </div>
  </div>
);

// ===== ORDER CARD WITH ITEM-LEVEL CONTROLS =====
const OrderCard = ({ order, kitchenType, filterOrderItems, updateItemStatus, bulkUpdateItems, menuPrepTimes = {}, printOrderTicket }) => {
  const items = filterOrderItems(order);
  const timeSince = getTimeSince(order.created_at);

  // Force re-render every 30s so overdue/timer stays fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(i);
  }, []);

  // Compute waited minutes for this order (based on creation)
  const waitedMinutes = order.created_at ? Math.floor((Date.now() - new Date(order.created_at).getTime()) / 60000) : 0;

  // Card is overdue if any active item has waited longer than its menu prep_time
  const activeItemsForOverdue = items.filter(i => i.item_status !== "served" && i.item_status !== "cancelled" && i.item_status !== "ready");
  const maxPrepTime = activeItemsForOverdue.length > 0
    ? Math.max(...activeItemsForOverdue.map(i => menuPrepTimes[i.menu_item_id] ?? menuPrepTimes[i.item_id] ?? 15))
    : 0;
  const isOverdue = activeItemsForOverdue.length > 0 && waitedMinutes > maxPrepTime;

  // Can we do a bulk action? All active items at same status
  const activeItems = items.filter(i => i.item_status !== "served" && i.item_status !== "cancelled");
  const allSameStatus = activeItems.length > 0 && activeItems.every(i => (i.item_status || "waiting") === (activeItems[0].item_status || "waiting"));
  const bulkNextStatus = allSameStatus && ITEM_STATUS_FLOW[activeItems[0].item_status || "waiting"];

  return (
    <div className={cn(
      "bg-gray-800 rounded-xl p-4 transition-all",
      isOverdue
        ? "border-2 border-red-500 ring-2 ring-red-500/40 animate-pulse"
        : "border border-gray-700"
    )} data-testid={`order-card-${order.id}`}>
      {/* Order header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-white font-mono">#{order.order_number.split("-")[1]}</span>
          <Badge className={cn("text-xs", order.order_type === "eat_in" ? "bg-blue-600 text-white" : "bg-orange-600 text-white")}>
            {order.order_type === "eat_in" ? `T${order.table_number || "-"}` : "TW"}
          </Badge>
        </div>
        <div className={cn("flex items-center gap-1 text-xs font-mono",
          isOverdue ? "text-red-400 font-bold" : "text-gray-400"
        )}>
          <Clock className="w-3 h-3" />
          <span>{timeSince}</span>
          {isOverdue && <span className="ml-1 bg-red-600 text-white px-1.5 py-0.5 rounded text-[10px]">OVERDUE</span>}
          <Button variant="ghost" size="icon" className="h-6 w-6 ml-1 text-gray-400 hover:text-white hover:bg-gray-700"
            onClick={() => printOrderTicket?.(order)} data-testid={`print-ticket-${order.id}`} title="Print kitchen ticket">
            <Printer className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Item-level controls */}
      <div className="space-y-2 mb-3">
        {items.map(item => {
          const status = item.item_status || "waiting";
          const nextStatus = ITEM_STATUS_FLOW[status];
          const isDone = status === "served" || status === "cancelled";
          
          return (
            <div key={item.id} className={cn(
              "flex items-center gap-2 p-2.5 rounded-lg transition-all",
              isDone ? "bg-green-900/20 opacity-60" :
              status === "preparing" ? "bg-amber-900/20 border border-amber-700/50" :
              status === "ready" ? "bg-green-900/30 border border-green-700/50" :
              "bg-gray-700/50"
            )} data-testid={`item-${item.id}`}>
              <span className="bg-gray-700 text-white px-1.5 py-0.5 rounded text-xs font-bold min-w-[24px] text-center">
                {item.quantity}x
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className={cn("text-sm font-medium truncate", isDone ? "line-through text-gray-500" : "text-white")}>{item.name}</span>
                  {item.variant_name && <span className="text-xs text-gray-400">({item.variant_name})</span>}
                </div>
                {item.extras?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {item.extras.map((e, i) => <span key={i} className="text-green-400 text-xs">+{e.name}</span>)}
                  </div>
                )}
                {item.notes && <p className="text-amber-400 text-xs mt-0.5">Note: {item.notes}</p>}
              </div>

              {/* Item status badge + action button */}
              <div className="flex items-center gap-1.5 shrink-0">
                {isDone ? (
                  <Check className="w-5 h-5 text-green-500" />
                ) : (
                  <>
                    <Badge className={cn("text-xs py-0",
                      status === "waiting" && "bg-gray-600 text-gray-300",
                      status === "preparing" && "bg-amber-500 text-black",
                      status === "ready" && "bg-green-500 text-white"
                    )}>
                      {status}
                    </Badge>
                    {nextStatus && (
                      <Button size="sm" className={cn("h-7 px-2.5 text-xs font-bold rounded-lg", ITEM_STATUS_COLORS[status])}
                        onClick={() => updateItemStatus(order.id, item.id, nextStatus)}
                        data-testid={`item-${item.id}-action`}>
                        {ITEM_STATUS_LABELS[status]}
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bulk action */}
      {bulkNextStatus && activeItems.length > 1 && (
        <Button className={cn("w-full h-10 text-sm font-bold", ITEM_STATUS_COLORS[activeItems[0].item_status || "waiting"])}
          onClick={() => bulkUpdateItems(order.id, bulkNextStatus)}
          data-testid={`bulk-action-${order.id}`}>
          {ITEM_STATUS_LABELS[activeItems[0].item_status || "waiting"]} All ({activeItems.length} items)
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      )}

      {/* Payment info */}
      <div className="flex items-center justify-between pt-2 mt-2 border-t border-gray-700">
        <span className="text-gray-500 text-xs">Payment</span>
        <Badge className={cn("text-xs", order.payment_status === "paid" ? "bg-green-600" : "bg-red-600")}>{order.payment_status === "paid" ? "Paid" : "Unpaid"}</Badge>
      </div>
    </div>
  );
};

export default KitchenDashboard;
